/**
 * Admin Onboarding Invites Management
 * 
 * Admins can:
 * - Create invite links for new employees
 * - View invite history and status
 * - Resend or revoke invites
 */

import React, { useState, useEffect } from 'react';
import {
  Mail, Plus, Copy, Check, X, Loader2, Search, Calendar,
  AlertCircle, Send, ChevronRight, MoreVertical
} from 'lucide-react';
import { backendApi } from '../../api/backendApi';
import toast from 'react-hot-toast';

const STATUSES = {
  pending: { label: 'Pending', color: '#ff9800', bgColor: '#fff3e0' },
  started: { label: 'Started', color: '#2196f3', bgColor: '#e3f2fd' },
  submitted: { label: 'Submitted', color: '#673ab7', bgColor: '#f3e5f5' },
  approved: { label: 'Approved', color: '#4caf50', bgColor: '#e8f5e9' },
  rejected: { label: 'Rejected', color: '#f44336', bgColor: '#ffebee' },
  completed: { label: 'Completed', color: '#4caf50', bgColor: '#e8f5e9' },
};

export default function OnboardingInvitesPage() {
  const [activeTab, setActiveTab] = useState('create');
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Create invite form
  const [formData, setFormData] = useState({
    email: '',
    positionId: '',
    departmentId: '',
    locationId: '',
    reportingManagerId: '',
  });
  const [creating, setCreating] = useState(false);
  const [positions, setPositions] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Load invite history
  useEffect(() => {
    loadInvites();
    loadLookupData();
  }, [statusFilter]);

  const loadInvites = async () => {
    try {
      setLoading(true);
      const invites = await backendApi.onboarding.listInvites({ status: statusFilter || undefined });
      setInvites(invites);
    } catch (error) {
      toast.error('Failed to load invites');
    } finally {
      setLoading(false);
    }
  };

  const loadLookupData = async () => {
    try {
      // Load positions and departments for dropdowns
      const [posRes, deptRes] = await Promise.all([
        backendApi.entities.Designation.list(),
        backendApi.entities.Department.list(),
      ]);
      setPositions(Array.isArray(posRes) ? posRes : (posRes.rows || []));
      setDepartments(Array.isArray(deptRes) ? deptRes : (deptRes.rows || []));
    } catch (error) {
      console.error('Failed to load lookup data:', error);
    }
  };

  const handleCreateInvite = async (e) => {
    e.preventDefault();

    if (!formData.email) {
      toast.error('Email is required');
      return;
    }

    try {
      setCreating(true);
      await backendApi.onboarding.createInvite(formData);

      toast.success('Invite created successfully!');
      setFormData({
        email: '',
        positionId: '',
        departmentId: '',
        locationId: '',
        reportingManagerId: '',
      });

      // Reload invites
      loadInvites();
    } catch (error) {
      toast.error(error.message || 'Failed to create invite');
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredInvites = invites.filter(invite => {
    if (searchQuery && !invite.email.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  return (
    <div className="ac-page">
      <div style={{ marginBottom: '30px' }}>
        <h1 style={styles.title}>Employee Onboarding</h1>
        <p style={styles.subtitle}>Manage invites and track new employee onboarding progress</p>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          onClick={() => setActiveTab('create')}
          style={{
            ...styles.tab,
            borderBottom: activeTab === 'create' ? '3px solid #667eea' : '3px solid transparent',
            color: activeTab === 'create' ? '#667eea' : '#666',
          }}
        >
          <Plus size={18} style={{ marginRight: '8px' }} />
          Create Invite
        </button>
        <button
          onClick={() => setActiveTab('history')}
          style={{
            ...styles.tab,
            borderBottom: activeTab === 'history' ? '3px solid #667eea' : '3px solid transparent',
            color: activeTab === 'history' ? '#667eea' : '#666',
          }}
        >
          <Mail size={18} style={{ marginRight: '8px' }} />
          Invite History ({invites.length})
        </button>
      </div>

      {/* Create Tab */}
      {activeTab === 'create' && (
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Create New Invite</h2>
          <p style={styles.cardDescription}>
            Generate an invite link for a new employee. They'll receive an email with the link to complete their onboarding.
          </p>

          <form onSubmit={handleCreateInvite} style={styles.form}>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email Address *</label>
                <input
                  type="email"
                  placeholder="newemployee@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Position</label>
                <select
                  value={formData.positionId}
                  onChange={(e) => setFormData({ ...formData, positionId: e.target.value })}
                  style={styles.input}
                >
                  <option value="">Select Position</option>
                  {positions.map(pos => (
                    <option key={pos.id} value={pos.id}>{pos.name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Department</label>
                <select
                  value={formData.departmentId}
                  onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                  style={styles.input}
                >
                  <option value="">Select Department</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Location</label>
                <input
                  type="text"
                  placeholder="Office location"
                  value={formData.locationId}
                  onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                  style={styles.input}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={creating}
              style={{
                ...styles.button,
                opacity: creating ? 0.7 : 1,
              }}
            >
              {creating ? (
                <>
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  Creating...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Create & Send Invite
                </>
              )}
            </button>
          </form>

          <div style={styles.infoBox}>
            <AlertCircle size={18} />
            <div>
              <strong>How it works:</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                <li>Employee receives an email with the invite link</li>
                <li>Link expires in 7 days</li>
                <li>Employee fills out comprehensive onboarding form</li>
                <li>You'll see the submission in the reviews section</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div>
          {/* Filters */}
          <div style={styles.filtersBar}>
            <input
              type="text"
              placeholder="Search by email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />

            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
              }}
              style={styles.filterSelect}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="started">Started</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Invites List */}
          {loading ? (
            <div style={styles.loadingContainer}>
              <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : filteredInvites.length === 0 ? (
            <div style={styles.emptyState}>
              <Mail size={48} style={{ color: '#ccc' }} />
              <h3>No invites yet</h3>
              <p>Create an invite to get started</p>
            </div>
          ) : (
            <div style={styles.invitesList}>
              {filteredInvites.map((invite) => (
                <InviteCard
                  key={invite.id}
                  invite={invite}
                  onCopy={copyToClipboard}
                  copiedId={copiedId}
                  onRefresh={loadInvites}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function InviteCard({ invite, onCopy, copiedId, onRefresh }) {
  const status = STATUSES[invite.status] || STATUSES.pending;
  const inviteLink = `${window.location.origin}/onboarding/${invite.token}`;
  const createdDate = new Date(invite.created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const expiresDate = new Date(invite.expires_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  const isExpired = new Date(invite.expires_at) < new Date();

  return (
    <div style={{
      ...styles.card,
      borderLeft: `4px solid ${status.color}`,
      backgroundColor: isExpired ? '#fafafa' : '#fff',
    }}>
      <div style={styles.inviteHeader}>
        <div style={styles.inviteMainInfo}>
          <h3 style={styles.inviteEmail}>{invite.email}</h3>
          <div style={styles.inviteMeta}>
            <span style={styles.metaItem}>
              <Calendar size={14} />
              Created {createdDate}
            </span>
            {!isExpired && (
              <span style={styles.metaItem}>
                Expires {expiresDate}
              </span>
            )}
            {isExpired && (
              <span style={{ ...styles.metaItem, color: '#f44336' }}>
                ⚠ Expired
              </span>
            )}
          </div>
        </div>

        <div style={styles.inviteRight}>
          <div style={{
            ...styles.statusBadge,
            backgroundColor: status.bgColor,
            color: status.color,
          }}>
            {status.label}
          </div>
        </div>
      </div>

      {/* Link Section */}
      <div style={styles.linkSection}>
        <input
          type="text"
          value={inviteLink}
          readOnly
          style={styles.linkInput}
        />
        <button
          onClick={() => onCopy(inviteLink, invite.id)}
          style={styles.copyButton}
          title="Copy link"
        >
          {copiedId === invite.id ? (
            <Check size={18} style={{ color: '#4caf50' }} />
          ) : (
            <Copy size={18} />
          )}
        </button>
      </div>

      {/* Actions */}
      <div style={styles.inviteActions}>
        {!isExpired && (
          <>
            <button style={styles.actionButton}>
              <Send size={14} />
              Resend Email
            </button>
          </>
        )}
        <button style={{ ...styles.actionButton, color: '#999' }}>
          <MoreVertical size={14} />
        </button>
      </div>
    </div>
  );
}

const styles = {
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    margin: '5px 0 0 0',
  },
  tabs: {
    display: 'flex',
    gap: '30px',
    borderBottom: '1px solid #e0e0e0',
    marginBottom: '30px',
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 0',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  card: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '20px',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333',
    margin: '0 0 8px 0',
  },
  cardDescription: {
    fontSize: '13px',
    color: '#666',
    margin: '0 0 24px 0',
  },
  form: {
    marginBottom: '24px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '16px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '13px',
    fontWeight: '500',
    marginBottom: '6px',
    color: '#333',
  },
  input: {
    padding: '10px 12px',
    border: '1px solid #d0d0d0',
    borderRadius: '6px',
    fontSize: '13px',
    fontFamily: 'inherit',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: '#667eea',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  infoBox: {
    display: 'flex',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#e3f2fd',
    border: '1px solid #90caf9',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#1976d2',
  },
  filtersBar: {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
  },
  searchInput: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid #d0d0d0',
    borderRadius: '6px',
    fontSize: '13px',
  },
  filterSelect: {
    padding: '10px 12px',
    border: '1px solid #d0d0d0',
    borderRadius: '6px',
    fontSize: '13px',
    minWidth: '150px',
  },
  invitesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  inviteHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  },
  inviteMainInfo: {
    flex: 1,
  },
  inviteEmail: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#333',
    margin: '0 0 6px 0',
  },
  inviteMeta: {
    display: 'flex',
    gap: '16px',
    fontSize: '12px',
    color: '#999',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  inviteRight: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '500',
  },
  linkSection: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
  },
  linkInput: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid #d0d0d0',
    borderRadius: '6px',
    fontSize: '12px',
    backgroundColor: '#fafafa',
    fontFamily: 'monospace',
  },
  copyButton: {
    padding: '8px 12px',
    border: '1px solid #d0d0d0',
    borderRadius: '6px',
    background: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  inviteActions: {
    display: 'flex',
    gap: '8px',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    fontSize: '12px',
    border: '1px solid #d0d0d0',
    borderRadius: '6px',
    background: '#fff',
    cursor: 'pointer',
    color: '#667eea',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '300px',
    color: '#667eea',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#999',
  },
};
